<script setup lang="ts">
import type { CarouselApi } from '~/components/ui/carousel';

import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious
} from '~/components/ui/carousel';

import IconMaterialSymbolsLightChevronLeft from '~icons/material-symbols-light/chevron-left';
import IconMaterialSymbolsLightChevronRight from '~icons/material-symbols-light/chevron-right';

const props = defineProps<{
	images: string[];
}>();

const emit = defineEmits<{
	'image-change': [url: string];
}>();

const isMouseOver = ref(false);
const carouselApi = ref<CarouselApi | null>(null);
const currentIndex = ref(0);

const limitedImages = computed(() => props.images.slice(0, 10));

function setupApi(api: CarouselApi) {
	carouselApi.value = api;
	api?.on('select', () => {
		currentIndex.value = api?.selectedScrollSnap() ?? 0;
		const url = limitedImages.value[currentIndex.value];
		if (url) emit('image-change', url);
	});
	const initialUrl = limitedImages.value[0];
	if (initialUrl) emit('image-change', initialUrl);
}

function goTo(event: MouseEvent, index: number) {
	event.stopPropagation();
	event.preventDefault();
	carouselApi.value?.scrollTo(index);
}
</script>

<template>
	<div
		class="relative flex flex-col w-full h-auto lg:h-full items-center justify-center bg-transparent"
		@mouseenter="isMouseOver = true"
		@mouseleave="isMouseOver = false"
	>
		<Carousel
			class="w-full mb-4 lg:mt-4 md:mt-4"
			:opts="{ loop: false, align: 'center' }"
			@init-api="setupApi"
		>
			<CarouselContent>
				<CarouselItem
					v-for="(url, index) in limitedImages"
					:key="`${url}-${index}`"
					class="flex items-center justify-center h-[350px] w-[350px] md:h-[375px] md:w-[375px] lg:h-[55vh] lg:w-full lg:aspect-square lg:overflow-hidden lg:bg-primary-light-4"
				>
					<NuxtImg
						class="w-full h-full object-cover lg:h-auto lg:aspect-square"
						:src="url"
						:alt="`Imagem ${index + 1}`"
						:loading="index === 0 ? 'eager' : 'lazy'"
						format="webp"
						quality="85"
						:width="479"
						sizes="xs:375px md:375px lg:479px"
						densities="x1"
					/>
				</CarouselItem>
			</CarouselContent>

			<template v-if="limitedImages.length > 1">
				<CarouselPrevious
					:is-mouse-over-carousel="isMouseOver"
					class="left-3 hidden rounded-full border-none bg-white text-black hover:bg-white/90 focus:bg-white/90 focus:border-transparent disabled:pointer-events-none disabled:opacity-0 lg:flex"
				>
					<IconMaterialSymbolsLightChevronLeft class="size-8 stroke-2" />
				</CarouselPrevious>

				<CarouselNext
					:is-mouse-over-carousel="isMouseOver"
					class="right-3 hidden rounded-full border-none bg-white text-black hover:bg-white/90 focus:bg-white/90 focus:border-transparent disabled:pointer-events-none disabled:opacity-0 lg:flex"
				>
					<IconMaterialSymbolsLightChevronRight class="size-8 stroke-2" />
				</CarouselNext>
			</template>
		</Carousel>

		<!-- Dot indicators -->
		<div
			v-if="limitedImages.length > 1"
			class="relative inset-x-0 mx-auto flex h-4 w-fit items-center justify-center gap-2 rounded-full border border-white/10 bg-[rgba(102,102,102,0.30)] px-3 backdrop-blur-[6px]"
		>
			<button
				v-for="(_, index) in limitedImages"
				:key="`dot-${index}`"
				class="size-1 min-w-1 shrink-0 cursor-pointer transition-colors"
				:class="currentIndex === index ? 'bg-white' : 'bg-white/30'"
				@click="goTo($event, index)"
			/>
		</div>
	</div>
</template>
